// The enquiry form's variants. Which CTA the visitor pressed decides the wording, the
// programme questions asked and the subject line sales sees. Mirrors LeadRequest::ENQUIRY_TYPES,
// ::REQUIRED_CONTEXT and the option maps below it — keep both in step, because the server
// revalidates every one of them. Only the keys mirror: the eyebrow, title and intro are the
// visitor's wording, while ENQUIRY_TYPES' values are the internal label sales reads.

export type EnquiryType =
  | "quotation"
  | "consultation"
  | "programme_plan"
  | "platform_demo"
  | "business_case_demo";

export type ContextField =
  | "objective"
  | "participants"
  | "support_level"
  | "starting_option"
  | "mentor_pool"
  | "capability_focus"
  | "demo_focus"
  | "timeline";

export type Option = { value: string; label: string };

const options = (entries: Record<string, string>): Option[] =>
  Object.entries(entries).map(([value, label]) => ({ value, label }));

export const ROLES = [
  "HR/People",
  "L&D",
  "People Ops",
  "Business leader",
  "Procurement",
  "Other",
] as const;

export const OBJECTIVES = options({
  retention: "Improve retention",
  productivity: "Boost productivity",
  leadership: "Build leadership pipeline",
  onboarding: "Speed up onboarding",
  other: "Another objective",
});

export const SUPPORT_LEVELS = options({
  platform: "Platform only",
  launch: "Platform + launch support",
  managed: "Fully managed programme",
  unsure: "Not sure yet",
});

export const STARTING_OPTIONS = options({
  pilot: "Six-month pilot",
  annual: "Twelve-month programme",
  unsure: "Not sure yet",
});

export const MENTOR_POOLS = options({
  internal: "Internal mentors",
  external: "External FutureLab mentors",
  mixed: "A mix of both",
  unsure: "Not sure yet",
});

// Deliberately coarser than the seven capabilities Marketing::PlatformCapabilities lists: this
// routes a demo, it does not catalogue the product. Session management and engagement tracking
// open the same demo, as do AI summaries and reporting, so each pair is offered as one choice.
export const CAPABILITY_FOCUSES = options({
  full_workflow: "The full programme workflow",
  learning_journeys: "Learning journeys",
  matching: "Mentor matching",
  sessions: "Sessions and engagement",
  reporting: "AI summaries and reporting",
});

export const DEMO_FOCUSES = options({
  validate_case: "Validate the business case",
  see_platform: "See the platform",
  programme_design: "Discuss programme design",
  pricing_scope: "Confirm pricing and scope",
});

export const TIMELINES = options({
  "0_3_months": "Within 3 months",
  "3_6_months": "In 3–6 months",
  "6_12_months": "In 6–12 months",
  exploring: "Still exploring",
});

export const CONTEXT_FIELDS: Record<
  Exclude<ContextField, "participants">,
  { label: string; placeholder: string; options: Option[] }
> = {
  objective: {
    label: "Primary programme objective",
    placeholder: "Select an objective",
    options: OBJECTIVES,
  },
  support_level: {
    label: "Support required",
    placeholder: "Select support required",
    options: SUPPORT_LEVELS,
  },
  starting_option: {
    label: "Preferred starting option",
    placeholder: "Select an option",
    options: STARTING_OPTIONS,
  },
  mentor_pool: {
    label: "Mentor pool",
    placeholder: "Select a mentor approach",
    options: MENTOR_POOLS,
  },
  capability_focus: {
    label: "Which capability should we focus on?",
    placeholder: "Select a capability",
    options: CAPABILITY_FOCUSES,
  },
  demo_focus: {
    label: "What should the demo focus on?",
    placeholder: "Select a focus",
    options: DEMO_FOCUSES,
  },
  timeline: {
    label: "Target launch timing",
    placeholder: "Select a timeline",
    options: TIMELINES,
  },
};

export type Variant = {
  eyebrow: string;
  title: string;
  intro: string;
  submit: string;
  submitting: string;
  fields: readonly ContextField[];
};

export const VARIANTS: Record<EnquiryType, Variant> = {
  quotation: {
    eyebrow: "Formal quotation",
    title: "Request a formal quotation",
    intro:
      "Confirm the details behind your estimate. We'll check the scope, then send you an official quotation.",
    submit: "Request my quotation",
    submitting: "Sending your request…",
    fields: ["objective", "participants", "support_level", "starting_option", "mentor_pool", "timeline"],
  },
  consultation: {
    eyebrow: "Talk to us",
    title: "Let's talk about your programme",
    intro:
      "Tell us what you're planning. You don't need it all figured out — we'll take it from there.",
    submit: "Speak to the team",
    submitting: "Sending your request…",
    fields: ["participants", "support_level", "timeline"],
  },
  programme_plan: {
    eyebrow: "Recommended programme plan",
    title: "Tell us what you want mentoring to change",
    intro:
      "Your answers shape what we recommend: how the programme runs, what participants go through, and how much support you'll want from us.",
    submit: "Request my programme plan",
    submitting: "Sending your request…",
    fields: ["objective", "participants", "support_level", "timeline"],
  },
  platform_demo: {
    eyebrow: "Platform demonstration",
    title: "Book a demo around your programme",
    intro:
      "Tell us how your programme works and which capabilities matter most. We'll shape the demonstration around your needs.",
    submit: "Request my platform demo",
    submitting: "Sending your request…",
    fields: ["participants", "support_level", "capability_focus", "timeline"],
  },
  // The only variant that asks no programme questions: the estimate rides along in
  // estimateSummary, so re-asking for the size and objective behind it would be asking the visitor
  // to restate what the panel beside them already shows.
  business_case_demo: {
    eyebrow: "Business case follow-up",
    title: "Book a demo around your business case",
    intro:
      "Your estimate comes with you. Tell us what you want to validate and when you hope to launch.",
    submit: "Request my demo",
    submitting: "Sending your request…",
    fields: ["demo_focus", "timeline"],
  },
};

export type ReassuranceIcon = "check" | "lock";

// Shown on the left rail. Sets expectations before the visitor spends time on the form. The last
// line answers a different worry from the two above it — what happens to their details, not what
// the request commits them to — so it carries its own icon rather than a third tick.
export const REASSURANCES: readonly { icon: ReassuranceIcon; text: string }[] = [
  { icon: "check", text: "No commitment — this just starts the conversation." },
  { icon: "check", text: "Any price you have seen is a ballpark until we scope it together." },
  { icon: "lock", text: "We'll only use your details to reply about FutureLab." },
];
